import { ID_CONTENU } from '@/components/layout/lien-evitement';
import { ChoixNoyauxAjout } from '@/components/noyaux/choix-noyaux-ajout';
import {
  ID_PANNEAU_RESULTAT,
  OngletsNoyaux,
  idOngletResultat,
} from '@/components/noyaux/onglets-noyaux';
import { TitreDocument } from '@/components/ui/titre-document';
import {
  ServiceIndisponibleError,
  SessionExpireeError,
  TraductionIntrouvableError,
} from '@/lib/api';
import { preparerRelance, viderBrouillon } from '@/lib/brouillon';
import { useToast } from '@/lib/notifications';
import { type ChoixResultat, ONGLET_ORIGINE } from '@/lib/params-recherche';
import { lireSession } from '@/lib/session';
import { attendre, inscrireSuivi, retirerSuivi } from '@/lib/suivi-traductions';
import { useTitrePage } from '@/lib/titre-page';
import { useInterruption } from '@/lib/use-interruption';
import { cleTraduction, useLectureTraduction } from '@/lib/use-lecture-traduction';
import { Spinner } from '@pms-digital/design-system';
import type { NoyauCode, Traduction } from '@rosetta/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { BandeauPartiel } from './bandeau-partiel';
import { CarteNonAboutie } from './carte-non-aboutie';
import { CarteResultat } from './carte-resultat';
import { EcranAttente } from './ecran-attente';
import { EcranEchec } from './ecran-echec';
import { EnteteResultat } from './entete-resultat';
import { ErreurService } from './erreur-service';
import { RedirectionInterrompue } from './redirection-interrompue';
import { RedirectionLogin } from './redirection-login';
import { nomFichierTexteOrigine, nomFichierTraduction, telechargerTexte } from './telechargement';
import { TraductionIntrouvable } from './traduction-introuvable';

interface EcranResultatProps {
  /** Identifier of the translation (the `$id` of the route). */
  id: string;
  /**
   * The `?noyau=` of the URL: a noyau or `origine` (#38), ignored when it is not one of the tabs
   * shown.
   */
  noyau: ChoixResultat | undefined;
  onChoisirOnglet: (choix: ChoixResultat) => void;
}

/** A blip of the service is retried twice; every other error is final. */
const NOMBRE_ESSAIS = 2;
const reessayerSiServiceIndisponible = (essais: number, erreur: Error) =>
  erreur instanceof ServiceIndisponibleError && essais < NOMBRE_ESSAIS;

export const MESSAGE_COPIE = 'Texte copié.';
export const MESSAGE_COPIE_ECHOUEE = "La copie n'a pas abouti.";

/** The statuses a translation does not leave on its own: the screen stops waiting for it. */
const STATUTS_DE_FIN = ['terminee', 'partielle', 'echouee', 'interrompue', 'bloquee'];

interface EtatEnCoursProps {
  libelle: string;
  /** What the page is, for the title of the tab. */
  titre: string;
}

function EtatEnCours({ libelle, titre }: EtatEnCoursProps) {
  useTitrePage(titre);
  return (
    <output className="flex items-center gap-3 text-body text-gray-700">
      <Spinner size="sm" label="" />
      {libelle}
    </output>
  );
}

interface TraductionTermineeProps {
  traduction: Traduction;
  noyauDemande: ChoixResultat | undefined;
  onChoisirOnglet: (choix: ChoixResultat) => void;
  onReessayer: () => void;
  /** Opens the input screen with this text, for the given noyaux (#5, #13). */
  onRelancer: (noyaux: readonly NoyauCode[]) => void;
}

/**
 * The finished translation: the header, the title of the document (#31, the component of the
 * analysis, below the header), the tabs ("Texte d'origine" first, #38, then the chosen noyaux) and the card. A partial
 * one (#13) has the same page, with a banner over the tabs and, on the tab of a noyau that did not
 * succeed, a card that says so instead of a text.
 */
function TraductionTerminee({
  traduction,
  noyauDemande,
  onChoisirOnglet,
  onReessayer,
  onRelancer,
}: TraductionTermineeProps) {
  const navigate = useNavigate();
  const { afficher } = useToast();
  const client = useQueryClient();
  // The choice of the noyaux to add (#24, the same window as the menu of a card of the history).
  const [choixOuvert, setChoixOuvert] = useState(false);
  useTitrePage('Votre texte traduit');

  const textes = new Map(
    traduction.resultats.flatMap((resultat) =>
      resultat.statut === 'terminee' && resultat.texte !== undefined
        ? [[resultat.noyau, resultat.texte] as const]
        : [],
    ),
  );
  // A noyau another launch of the document is translating (#36) is listed apart, with its ring: it
  // is no tab, even when its text is already there (that launch cannot be read until its quality
  // control is over), and a noyau that did not succeed here and is being translated again is not
  // "en échec".
  const enCours = traduction.noyauxEnCours ?? [];
  const reprises = new Set(enCours.map((ligne) => ligne.noyau));
  const onglets = traduction.noyaux.filter((code) => !reprises.has(code));
  const enEchec = onglets.filter((code) => !textes.has(code));
  // By default the first noyau that has a text: a partial translation opens on something to read.
  const premier = onglets.find((code) => textes.has(code)) ?? onglets[0];
  // The text as it was entered (#38): a tab as soon as it is known, read with the translation (no
  // call more), never the tab shown by default.
  const texteOriginal = traduction.texteOrigine.trim() === '' ? undefined : traduction.texteOrigine;
  let actif: ChoixResultat | undefined = premier;
  if (noyauDemande === ONGLET_ORIGINE) {
    if (texteOriginal !== undefined) actif = ONGLET_ORIGINE;
  } else if (noyauDemande !== undefined && onglets.includes(noyauDemande)) {
    actif = noyauDemande;
  }
  // Cannot happen (the schema asks for one noyau at least), but the type says so.
  if (actif === undefined) return <ErreurService onReessayer={onReessayer} />;

  const texte = actif === ONGLET_ORIGINE ? texteOriginal : textes.get(actif);

  /**
   * The add is launched (#24): followed like any translation, so the toast T1 says when it is done;
   * the window closes and the page stays on this result, which reads the translation again when
   * the add ends, so the new noyau joins the tabs.
   */
  function ajoutLance(ajout: string, noyaux: readonly NoyauCode[]) {
    setChoixOuvert(false);
    const session = lireSession();
    if (session !== null) {
      inscrireSuivi({
        id: ajout,
        titre: traduction.titre ?? '',
        utilisateurId: session.utilisateur.id,
      });
    }
    afficher({ variant: 'neutre', message: `La traduction vers ${noyaux.join(', ')} est lancée.` });
    // Read again at once: the noyaux just launched join the page with their ring (#36).
    void client.invalidateQueries({ queryKey: cleTraduction(traduction.id) });
  }

  function nouveauTexte() {
    viderBrouillon();
    void navigate({ to: '/' });
  }

  async function copier() {
    try {
      await navigator.clipboard.writeText(texte ?? '');
      afficher({ variant: 'neutre', message: MESSAGE_COPIE });
    } catch {
      afficher({ variant: 'erreur', message: MESSAGE_COPIE_ECHOUEE });
    }
  }

  function telecharger() {
    if (actif === undefined || texte === undefined) return;
    telechargerTexte(
      actif === ONGLET_ORIGINE
        ? nomFichierTexteOrigine(traduction.titre)
        : nomFichierTraduction(traduction.titre, actif),
      texte,
    );
  }

  return (
    <>
      <EnteteResultat onAutreNoyau={() => setChoixOuvert(true)} onNouveau={nouveauTexte} />
      <TitreDocument titre={traduction.titre} />
      {choixOuvert ? (
        <ChoixNoyauxAjout
          id={traduction.id}
          titre={traduction.titre}
          onFermer={() => setChoixOuvert(false)}
          onLance={ajoutLance}
        />
      ) : null}
      {enEchec.length > 0 ? (
        <BandeauPartiel
          noyaux={onglets}
          enEchec={enEchec}
          messageErreur={traduction.messageErreur}
          onRelancer={() => onRelancer(enEchec)}
        />
      ) : null}
      <div className="mt-6">
        <OngletsNoyaux
          noyaux={onglets}
          actif={actif}
          onChange={onChoisirOnglet}
          avecOrigine={texteOriginal !== undefined}
          enEchec={enEchec}
          enCours={enCours}
        />
      </div>
      <div
        role="tabpanel"
        id={ID_PANNEAU_RESULTAT}
        aria-labelledby={idOngletResultat(actif)}
        className="mt-4"
      >
        {texte === undefined ? (
          actif === ONGLET_ORIGINE ? null : (
            <CarteNonAboutie noyau={actif} onRelancer={() => onRelancer(enEchec)} />
          )
        ) : (
          <CarteResultat
            traductionId={traduction.id}
            noyau={actif}
            texte={texte}
            onCopier={() => {
              void copier();
            }}
            onTelecharger={telecharger}
          />
        )}
      </div>
    </>
  );
}

/**
 * The result screen (R1): reads the translation, then shows it by noyau. Every state of the
 * read has its own rendering, all at the address of the translation: still running (the waiting
 * screen W1, read again every 5 s, #7: the result opens at the same address), blocked (W1 in its
 * "anomalie" state, no more reading, #13), finished or partial (#13: the tabs, the noyaux that did
 * not succeed say so), failed (#13: the page of the failure, with the message of Tahiti and
 * "relancer"; it no longer sends the user to an input screen that may hold another text, B4),
 * interrupted (toast and to the history), unknown (page with "nouveau texte"), expired session
 * (back to the login, which comes back here), service down ("réessayer").
 */
export function EcranResultat({ id, noyau, onChoisirOnglet }: EcranResultatProps) {
  const navigate = useNavigate();
  // A reload during the treatment: read again every 5 s until a final status, even through a
  // blip of the service.
  const requete = useLectureTraduction(id, {
    retry: reessayerSiServiceIndisponible,
    poursuivreSurErreur: true,
  });
  const interruption = useInterruption({ versHistorique: true });
  // Read in the render so that the screen is drawn again at each read (the time elapsed).
  const { data, error, dataUpdatedAt } = requete;
  const statut = data?.statut;

  // This screen shows the outcome itself, so the follow-up of the whole app leaves the
  // translation alone while it is open, and forgets it once it has ended (#7).
  useEffect(() => attendre(id), [id]);
  useEffect(() => {
    if (statut !== undefined && STATUTS_DE_FIN.includes(statut)) retirerSuivi(id);
  }, [id, statut]);

  // The waiting screen gives way to the result, the failure or the anomaly at the same address:
  // no change of page, so no focus sent to the content by the root (S14, #5), and the control the
  // user was on (or the page, if the focus was lost with it) would leave the keyboard nowhere. It
  // goes to the content.
  const attendait = useRef(false);
  useEffect(() => {
    if (statut === 'en_cours') {
      attendait.current = true;
    } else if (statut !== undefined && statut !== 'interrompue' && attendait.current) {
      attendait.current = false;
      const principal = document.getElementById(ID_CONTENU);
      const actif = document.activeElement;
      if (actif === null || actif === document.body || principal?.contains(actif)) {
        principal?.focus({ preventScroll: true });
      }
    }
  }, [statut]);

  function reessayer() {
    void requete.refetch();
  }

  /** "relancer" (#5): the input screen, filled with this text and these noyaux; nothing is sent. */
  function relancer(traduction: Traduction, noyaux: readonly NoyauCode[] = traduction.noyaux) {
    preparerRelance({ ...traduction, noyaux });
    void navigate({ to: '/' });
  }

  if (error instanceof SessionExpireeError) return <RedirectionLogin />;

  if (error instanceof TraductionIntrouvableError) {
    return (
      <TraductionIntrouvable
        onNouveauTexte={() => {
          viderBrouillon();
          void navigate({ to: '/' });
        }}
      />
    );
  }

  // Any other error is the service: it ended a series of retries (see `reessayerSiServiceIndisponible`).
  if (data === undefined) {
    return error === null ? (
      <EtatEnCours libelle="Chargement de la traduction..." titre="Chargement de la traduction" />
    ) : (
      <ErreurService onReessayer={reessayer} />
    );
  }

  if (data.statut === 'echouee') {
    return <EcranEchec traduction={data} onRelancer={() => relancer(data)} />;
  }
  const interruptionEnCours = interruption.enCours.has(id);
  // Interrupted elsewhere, or earlier: to the history. Interrupted by the click that is still
  // being handled here: the interruption announces itself and takes the user to the history.
  if (data.statut === 'interrompue' && !interruptionEnCours) return <RedirectionInterrompue />;
  if (data.statut === 'en_cours' || data.statut === 'bloquee' || data.statut === 'interrompue') {
    return (
      <EcranAttente
        traduction={data}
        luLe={dataUpdatedAt}
        interruptionEnCours={interruptionEnCours}
        onInterrompre={() => {
          void interruption.interrompre(id);
        }}
        onRelancer={() => relancer(data)}
      />
    );
  }

  return (
    <TraductionTerminee
      traduction={data}
      noyauDemande={noyau}
      onChoisirOnglet={onChoisirOnglet}
      onReessayer={reessayer}
      onRelancer={(noyaux) => relancer(data, noyaux)}
    />
  );
}
